import { DialogWithForm } from '@strata/react-dialog';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { type SelectValue } from '@strata/react-selects/dist/src/services/helpers';
import { SuccessToast } from '@strata/react-toast-message';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { useGetLighthouseUserNameMapQuery, useGetUserProfileQuery } from 'store/api/authApi';
import { type IntakeCase, usePostCaseAssignmentMutation } from 'store/api/intakeCaseApi';

const ValidationSchema = Yup.object({
    assignedUsername: Yup.string().nullable(),
});

interface CaseAssignmentDialogProps {
    isOpen: boolean;
    onClose: () => void;
    intakeCase: Pick<IntakeCase, 'intakeCaseId' | 'assignedUsername'>;
}

const buildSelectValue = (name: string, secondaryData?: string): SelectValue => ({
    name,
    secondaryData: secondaryData ?? '',
});

export const CaseAssignmentDialog = ({
    isOpen,
    onClose,
    intakeCase,
}: CaseAssignmentDialogProps) => {
    const [postAssignCase] = usePostCaseAssignmentMutation();
    const { data: userNameMap } = useGetLighthouseUserNameMapQuery();
    const { data: profile } = useGetUserProfileQuery();
    const options = [];
    const currentAssignedUser = intakeCase.assignedUsername;
    if (currentAssignedUser !== null) {
        options.push(buildSelectValue('Unassign', undefined));
    }
    options.push(buildSelectValue('Assign to myself', profile?.email));
    const otherUsers = Object.entries(userNameMap ?? {})
        .filter((x) => x[1] !== profile?.fullName)
        .map((x) => buildSelectValue(x[1], x[0]));
    options.push(...otherUsers);
    const tempUserList = options;

    const handleAssignedUser = (newValue: SelectValue): string | null => {
        if (typeof newValue === 'object') {
            switch (newValue.name) {
                case 'Unassign':
                    return '';
                case 'Assign to myself':
                    return profile?.email ?? '';
                default:
                    return typeof newValue.secondaryData === 'string' ? newValue.secondaryData : '';
            }
        }
        return '';
    };
    return (
        <Formik
            initialValues={{
                assignedUsername:
                    intakeCase.assignedUsername === ''
                        ? 'Unassigned'
                        : (intakeCase.assignedUsername ?? ''),
            }}
            validationSchema={ValidationSchema}
            enableReinitialize
            onSubmit={async (values, { resetForm }) => {
                await postAssignCase({
                    intakeCaseId: intakeCase.intakeCaseId,
                    assignedUsername: values.assignedUsername,
                }).unwrap();
                SuccessToast({ message: 'Case assigned successfully.' });
                resetForm();
                onClose();
            }}
        >
            {(formik) => (
                <DialogWithForm
                    isOpen={isOpen}
                    dialogTitle="Select/Change Assignment"
                    isPrimaryButtonDisabled={
                        !(formik.dirty && formik.isValid) || formik.isSubmitting
                    }
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel="Save"
                    secondaryButtonLabel="Cancel"
                    dialogContent={
                        <SuggestSingleSelectFormik
                            id="assignedUsername"
                            label="Select/Change Assignment"
                            placeholderText="Select"
                            required
                            values={tempUserList}
                            onChange={(newValue) => {
                                void Promise.all([
                                    formik.setFieldValue(
                                        'assignedUsername',
                                        handleAssignedUser(newValue!),
                                    ),
                                ]);
                            }}
                            disableAutoPopulate
                        />
                    }
                />
            )}
        </Formik>
    );
};
